import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";
import { MaskLine } from "@/components/Reveal";

const HERO_BG = "https://images.unsplash.com/photo-1710438399422-2fca27686bcd";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen w-full overflow-hidden">
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_BG}
          alt="Abstract AI texture"
          className="h-full w-full object-cover opacity-40 grayscale contrast-125"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/70 to-[#050505]" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-center pt-24 pb-20"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-gradient-to-r from-[#4f6bff] to-[#9d6bff]" />
          <span className="overline text-[#8b7cff]/90">GenAI · Agentic AI · Engineering</span>
        </motion.div>

        <h1 className="font-display font-900 tracking-tighter leading-[0.92] text-white text-4xl sm:text-5xl lg:text-6xl max-w-5xl">
          <MaskLine delay={0.15}>We build the</MaskLine>
          <MaskLine delay={0.28} className="bg-gradient-to-r from-[#4f6bff] via-[#7b6bff] to-[#9d6bff] bg-clip-text text-transparent">autonomous systems</MaskLine>
          <MaskLine delay={0.41}>that run tomorrow&apos;s</MaskLine>
          <MaskLine delay={0.54}>enterprise.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-9 max-w-xl text-base sm:text-lg text-white/60 leading-relaxed"
        >
          Agentica AI Solutions is a consulting and engineering studio helping ambitious
          teams design, build, and deploy production-grade Generative &amp; Agentic AI.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-11 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
            className="group inline-flex items-center gap-2 rounded-full bg-white text-void px-7 py-3.5 text-sm font-500 hover:bg-gradient-to-r hover:from-[#4f6bff] hover:to-[#9d6bff] hover:text-white transition-[background-color,color] duration-300"
            data-testid="hero-cta-primary"
          >
            Start a project
            <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
          </a>
          <a
            href="#services"
            onClick={(e) => { e.preventDefault(); document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" }); }}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white px-7 py-3.5 text-sm font-500 hover:border-white/60 hover:bg-white/5 transition-colors duration-300"
            data-testid="hero-cta-secondary"
          >
            Explore capabilities
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-3xl border-t border-white/10 pt-8"
        >
          {[
            ["40+", "Agents shipped"],
            ["12", "Enterprise clients"],
            ["3x", "Faster delivery"],
            ["99.9%", "Uptime SLAs"],
          ].map(([n, l]) => (
            <div key={l}>
              <div className="font-display text-3xl font-700 text-white tracking-tight">{n}</div>
              <div className="mt-1 text-xs text-white/45">{l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
